export const ASDP_CONTRACT = {
  version: 1,
  workflow: 'sdlc-setup-portal.yml',
  requestPath: '.vivicta/setup-proposal.json',
  controlPath: '.vivicta/control.json',
  assurancePath: '.vivicta/assurance.json',
} as const;

const QUESTIONS = {
  data: 'asdpData',
  regulation: 'asdpRegulation',
  contract: 'asdpContract',
  external_effect: 'asdpExternalEffect',
  reversibility: 'asdpReversibility',
  privilege: 'asdpPrivilege',
} as const;

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

export function asdpContract(manifest: unknown) {
  if (!isObject(manifest) || !isObject(manifest.spec) || manifest.spec.asdp === undefined) {
    return undefined;
  }
  const contract = manifest.spec.asdp;
  // `spec.asdp: { journey: true }` alone opts in to the journey, not to the v1 contract.
  if (isObject(contract) && contract.version === undefined && contract.journey === true) {
    return undefined;
  }
  if (!isObject(contract) ||Object.entries(ASDP_CONTRACT).some(([key, value]) => contract[key] !== value)) {
    throw new Error('Unsupported ASDP onboarding contract. Update the portal before creating this application.');
  }
  return ASDP_CONTRACT;
}

export function buildAsdpSetupRequest(input: unknown, trackingIssue: number) {
  if (!isObject(input) || !Number.isSafeInteger(trackingIssue) || trackingIssue < 1) {
    throw new Error('ASDP setup requires form values and a tracking issue.');
  }
  const text = (name: string) => {
    const value = input[name];
    if (typeof value !== 'string' || value.length === 0) {
      throw new Error(`ASDP setup requires ${name}.`);
    }
    return value;
  };
  const preset = text('asdpPreset');
  if (!['human-only', 'advisory', 'preparation', 'bounded-execution'].includes(preset)) {
    throw new Error('Unknown ASDP autonomy preset.');
  }
  const questionnaire = Object.fromEntries(Object.entries(QUESTIONS).map(([question, field]) => {
    const value = text(field);
    if (!['P1', 'P2', 'P3'].includes(value)) throw new Error(`Answer the ASDP ${question} question.`);
    return [question, value];
  }));
  const roles: unknown = JSON.parse(typeof input.asdpRoles === 'string' ? input.asdpRoles : '{}');
  if (!isObject(roles) || Object.values(roles).some(owner => typeof owner !== 'string' || !/^@[A-Za-z0-9-]+(?:\/[A-Za-z0-9-]+)?$/.test(owner))) {
    throw new Error('Role assignments must be a JSON object of GitHub people or teams.');
  }
  return {
    project: {
      initiative: { id: text('initiativeId'), name: text('projectName') },
      blueprint: 'go-service', role_organization: text('githubOrg'), roles,
      tracking_issue: trackingIssue,
      ...(input.asdpBoundary === 'isolated-experiment' && {
        p0_boundaries: JSON.parse(text('asdpP0Boundaries')) as Record<string, boolean>,
      }),
    },
    boundary: text('asdpBoundary'), preset, ssd_risk: text('asdpSsdRisk'), questionnaire,
  };
}

export const ASDP_SETUP_LABELS = {
  'configuration-required': 'Configuration required',
  'configuration-review': 'Configuration review',
  'assurance-required': 'Assurance required',
} as const;

export type AsdpSetupState = keyof typeof ASDP_SETUP_LABELS;

export function asdpSetupState(proposal: unknown, merged: boolean): AsdpSetupState {
  if (!isObject(proposal)) return 'configuration-required';
  return merged ? 'assurance-required' : 'configuration-review';
}